import { Injectable, OnDestroy, Signal, computed, inject, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApiClientConstant } from '@cureskin/api-client';
import { ChatService } from '@services/chat-service/chat-service';
import { AppConfig } from 'client/app/app.config';
import {
  INSTANT_CHECKUP_ACTION_NAME,
  QuizAnswerOutcome,
  QuizChatConfig,
  QuizChatMessage,
  QuizOption,
  QuizQuestionLayout,
} from './quiz-chat.types';

const { InputType, InputAction } = ApiClientConstant.Question;

/**
 * Generic one-question-at-a-time orchestration over the ChatService bot-tree engine.
 * Domain specifics (channel, completion predicate, skip list, resume nudge) arrive via
 * QuizChatConfig — see client/services/chat-service/CLAUDE.md for the consumer map.
 * Locally provided together with ChatService; never root.
 */
@Injectable()
export class QuizChatService implements OnDestroy {
  private readonly chatService = inject(ChatService);
  private readonly appConfig = inject(AppConfig);

  private readonly loadingState = signal<boolean>(true);
  private readonly questionTextState = signal<string>('');
  private readonly questionSubTextState = signal<string>('');
  private readonly layoutState = signal<QuizQuestionLayout | null>(null);
  private readonly imageOptionsState = signal<QuizOption[]>([]);
  private readonly buttonOptionsState = signal<QuizOption[]>([]);
  private readonly textPlaceholderState = signal<string>('');
  private readonly textCtaOptionState = signal<QuizOption | null>(null);
  private readonly photoCtaOptionState = signal<QuizOption | null>(null);
  private readonly questionNumberState = signal<number>(1);
  private readonly isThinkingState = signal<boolean>(false);
  private readonly isCompletedState = signal<boolean>(false);
  private readonly completionMessageState = signal<QuizChatMessage | null>(null);
  private readonly errorMessageState = signal<string>('');
  private readonly requiresFallbackState = signal<boolean>(false);
  private readonly messagesState = signal<QuizChatMessage[]>([]);

  readonly loading: Signal<boolean> = this.loadingState.asReadonly();
  readonly questionText: Signal<string> = this.questionTextState.asReadonly();
  readonly questionSubText: Signal<string> = this.questionSubTextState.asReadonly();
  readonly layout: Signal<QuizQuestionLayout | null> = this.layoutState.asReadonly();
  readonly imageOptions: Signal<QuizOption[]> = this.imageOptionsState.asReadonly();
  readonly buttonOptions: Signal<QuizOption[]> = this.buttonOptionsState.asReadonly();
  readonly textPlaceholder: Signal<string> = this.textPlaceholderState.asReadonly();
  readonly textCtaOption: Signal<QuizOption | null> = this.textCtaOptionState.asReadonly();
  readonly photoCtaOption: Signal<QuizOption | null> = this.photoCtaOptionState.asReadonly();
  readonly questionNumber: Signal<number> = this.questionNumberState.asReadonly();
  readonly isThinking: Signal<boolean> = this.isThinkingState.asReadonly();
  readonly isCompleted: Signal<boolean> = this.isCompletedState.asReadonly();
  /** The chat row that satisfied isCompletionMessage — consumers read completion metadata off it. */
  readonly completionMessage: Signal<QuizChatMessage | null> = this.completionMessageState.asReadonly();
  readonly errorMessage: Signal<string> = this.errorMessageState.asReadonly();
  readonly requiresFallback: Signal<boolean> = this.requiresFallbackState.asReadonly();
  /** The full chat history for the active thread — consumers read prior answers (e.g. uploaded photos). */
  readonly messages: Signal<QuizChatMessage[]> = this.messagesState.asReadonly();
  readonly hasActiveQuestion: Signal<boolean> = computed(() => this.layoutState() !== null);

  private config: QuizChatConfig | null = null;
  private latestBotMessage: QuizChatMessage | null = null;
  private readonly autoAnswered = new Set<string>();
  private readonly interstitialPolled = new Set<string>();
  private answerSaveInProcess = false;
  private emittedMessageCount = 0;
  private readonly subscriptions: Subscription[] = [];

  configure(config: QuizChatConfig): void {
    this.config = config;
  }

  /** Raw chat row of the currently displayed question — id/className/context for ACTION dispatch. */
  currentQuestion(): QuizChatMessage | null {
    return this.latestBotMessage;
  }

  /** questionId of the displayed question — the half of currentQuestion() the QuizEngine seam needs. */
  currentQuestionId(): string | undefined {
    return this.latestBotMessage?.get('questionId');
  }

  async start(): Promise<void> {
    const config = this.requireConfig();
    this.subscriptions.push(this.chatService.allChats$.subscribe(
      (messages) => this.applyMessages(messages as unknown as QuizChatMessage[]),
    ));
    this.subscriptions.push(this.chatService.showThinking$.subscribe(
      (thinking) => this.isThinkingState.set(thinking),
    ));
    try {
      await this.chatService.loadChatHistory(config.channel);
      await this.nudgeIfAwaitingBotReply();
    } catch (error) {
      this.errorMessageState.set(this.toErrorText(error));
    } finally {
      this.loadingState.set(false);
    }
  }

  /** Explicit answer submission — the page builds the message per layout and calls this on Continue. */
  async submitAnswer(message: string): Promise<QuizAnswerOutcome> {
    const config = this.requireConfig();
    if (!message || !this.latestBotMessage || this.answerSaveInProcess || this.isThinkingState()) {
      return QuizAnswerOutcome.Ignored;
    }
    this.answerSaveInProcess = true;
    this.errorMessageState.set('');
    try {
      await this.chatService.sendMessage(config.channel, this.latestBotMessage, message);
      return QuizAnswerOutcome.Sent;
    } catch (error) {
      this.chatService.setThinking(false);
      if (this.isPendingAnswersError(error)) {
        await this.chatService.loadChatHistory(config.channel);
      } else {
        this.errorMessageState.set(this.toErrorText(error));
      }
      // The answer did not go through — the page must not fire its success analytics.
      return QuizAnswerOutcome.Ignored;
    } finally {
      this.answerSaveInProcess = false;
    }
  }

  ngOnDestroy(): void {
    this.chatService.stopBotReplyTimer();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private applyMessages(messages: QuizChatMessage[]): void {
    this.messagesState.set(messages);
    this.emitNewMessages(messages);
    if (!messages.length) return;
    // Completion is checked across the whole batch (legacy chatV2 parity — addToChats evaluates
    // showAfterTime per message): the completion row is often NOT the latest one, e.g. the
    // allocated-regimen ARTICLE lands after the showAfterTime delay message.
    const completionRow = messages.find((message) => this.requireConfig().isCompletionMessage(message));
    if (completionRow) {
      this.markCompleted(completionRow);
      return;
    }
    const latest = this.getLatestActionableMessage(messages);
    if (!latest) return;
    if (this.isSkippableQuestion(latest)) {
      void this.triggerAutoAnswer(latest);
      this.clearQuestion();
      return;
    }
    if (this.isInterstitialMessage(latest)) {
      // Tree-end interstitial (skip-listed, no inputs — e.g. whatDoCureSkinMembersSay before the
      // regimen messages land). Legacy chatV2 treats it as "not a real reply" and keeps polling.
      this.clearQuestion();
      this.armInterstitialPolling(latest);
      return;
    }
    const inputs = latest.get('Inputs');
    const isActiveBotQuestion = latest.get('Owner') === this.appConfig.Shared.Assistant.Owner.BOT
      && Array.isArray(inputs) && inputs.length > 0;
    if (!isActiveBotQuestion) {
      // Awaiting the bot: keep the current question up (CTA spinner) instead of clearing to skeleton.
      if (latest.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER) return;
      this.clearQuestion();
      return;
    }
    // The chat state re-emits the same messages on unrelated updates (e.g. the thinking flag
    // flipping during submit). Re-classifying the same question would replace the option array
    // references, which resets the option components' visual selection mid-submit — skip it.
    if (this.isCurrentQuestion(latest)) {
      this.latestBotMessage = latest;
      return;
    }
    this.classifyQuestion(latest, inputs as QuizOption[], messages);
  }

  private isCurrentQuestion(latest: QuizChatMessage): boolean {
    return !!latest.id && latest.id === this.latestBotMessage?.id;
  }

  private classifyQuestion(latest: QuizChatMessage, inputs: QuizOption[], messages: QuizChatMessage[]): void {
    const presentInputs = inputs.filter((input) => !!input);
    const textInput = presentInputs.find((input) => input.type === InputType.TEXT);
    const imageOptions = presentInputs.filter((input) => input.type === InputType.IMAGE);
    const buttonOptions = presentInputs.filter((input) => input.type === InputType.BUTTON);

    let layout: QuizQuestionLayout | null = null;
    let photoOption: QuizOption | null = null;
    if (textInput) {
      layout = QuizQuestionLayout.Text;
    } else if (imageOptions.length || buttonOptions.length) {
      const selectable = [...imageOptions, ...buttonOptions];
      const hasTreeOption = selectable.some((input) => input.action === InputAction.TREE);
      photoOption = this.findPhotoOption(buttonOptions);
      if (!hasTreeOption && photoOption) {
        layout = QuizQuestionLayout.PhotoUpload;
      } else if (!hasTreeOption) {
        this.requiresFallbackState.set(true);
        this.clearQuestion();
        return;
      } else {
        layout = latest.get('Mode') === this.appConfig.Shared.Assistant.Mode.MULTI_SELECT
          ? QuizQuestionLayout.MultiSelect
          : QuizQuestionLayout.SingleSelect;
      }
    } else {
      this.requiresFallbackState.set(true);
      this.clearQuestion();
      return;
    }

    const isSelectableLayout = layout === QuizQuestionLayout.SingleSelect || layout === QuizQuestionLayout.MultiSelect;
    this.latestBotMessage = latest;
    this.layoutState.set(layout);
    this.questionTextState.set(latest.get('Message') || '');
    this.questionSubTextState.set(latest.get('subMessage') || '');
    this.imageOptionsState.set(isSelectableLayout ? imageOptions : []);
    this.buttonOptionsState.set(isSelectableLayout ? buttonOptions : []);
    this.textPlaceholderState.set(textInput?.text || '');
    this.textCtaOptionState.set(
      layout === QuizQuestionLayout.Text
        ? buttonOptions.find((input) => input.action === InputAction.TREE) || null
        : null,
    );
    this.photoCtaOptionState.set(layout === QuizQuestionLayout.PhotoUpload ? photoOption : null);
    this.questionNumberState.set(this.computeQuestionOrdinal(messages, latest));
  }

  /** Photo-upload questions carry a single ACTION button that opens the instant-checkup camera flow. */
  private findPhotoOption(buttonOptions: QuizOption[]): QuizOption | null {
    return buttonOptions.find(
      (input) => input.action === InputAction.ACTION && input.params?.actionName === INSTANT_CHECKUP_ACTION_NAME,
    ) || null;
  }

  private emitNewMessages(messages: QuizChatMessage[]): void {
    const onMessages = this.config?.onMessages;
    if (!onMessages) return;
    if (messages.length > this.emittedMessageCount) {
      onMessages(messages.slice(this.emittedMessageCount));
      this.emittedMessageCount = messages.length;
    } else if (messages.length < this.emittedMessageCount) {
      this.emittedMessageCount = messages.length;
    }
  }

  private getLatestActionableMessage(messages: QuizChatMessage[]): QuizChatMessage | undefined {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index].get('Mode') !== this.appConfig.Shared.Assistant.Mode.SMS_COPY) {
        return messages[index];
      }
    }
    return undefined;
  }

  private isSkippableQuestion(latest: QuizChatMessage): boolean {
    const questionId = latest.get('questionId');
    if (!questionId || !this.requireConfig().skipQuestionIds.includes(questionId)) return false;
    if (latest.get('Owner') !== this.appConfig.Shared.Assistant.Owner.BOT) return false;
    const inputs = latest.get('Inputs');
    return Array.isArray(inputs) && inputs.some(
      (input: QuizOption) => input?.action === InputAction.TREE && input?.type === InputType.BUTTON,
    );
  }

  /** Skip-listed BOT row with no answerable inputs — the tree-end testimonial variant. */
  private isInterstitialMessage(latest: QuizChatMessage): boolean {
    const questionId = latest.get('questionId');
    if (!questionId || !this.requireConfig().skipQuestionIds.includes(questionId)) return false;
    if (latest.get('Owner') !== this.appConfig.Shared.Assistant.Owner.BOT) return false;
    const inputs = latest.get('Inputs');
    return !Array.isArray(inputs) || inputs.length === 0;
  }

  /** Re-arm the poll loop once per interstitial row — state re-emissions must not reset the backoff. */
  private armInterstitialPolling(latest: QuizChatMessage): void {
    if (!latest.id || this.interstitialPolled.has(latest.id)) return;
    this.interstitialPolled.add(latest.id);
    this.chatService.startPollingForBotReply(this.requireConfig().channel);
  }

  private async triggerAutoAnswer(latest: QuizChatMessage): Promise<void> {
    if (!latest.id || this.autoAnswered.has(latest.id) || this.answerSaveInProcess) return;
    this.autoAnswered.add(latest.id);
    const treeButton = (latest.get('Inputs') as QuizOption[]).find(
      (input) => input?.action === InputAction.TREE && input?.type === InputType.BUTTON,
    );
    const message = treeButton?.text || treeButton?.value || '';
    if (!message) {
      this.autoAnswered.delete(latest.id);
      return;
    }
    try {
      await this.chatService.sendMessage(this.requireConfig().channel, latest, message);
    } catch (error) {
      this.autoAnswered.delete(latest.id);
      this.errorMessageState.set(this.toErrorText(error));
    }
  }

  private async nudgeIfAwaitingBotReply(): Promise<void> {
    const config = this.requireConfig();
    const latest = this.getLatestActionableMessage(
      this.chatService.currentMessages as unknown as QuizChatMessage[],
    );
    const awaitingBot = latest?.get('Owner') === this.appConfig.Shared.Assistant.Owner.USER
      && !!latest?.get('messageAttendedByBot');
    if (!awaitingBot) return;
    await config.resumeNudge?.();
    this.chatService.startPollingForBotReply(config.channel);
  }

  private markCompleted(message: QuizChatMessage): void {
    if (this.isCompletedState()) return;
    this.chatService.stopBotReplyTimer();
    this.isThinkingState.set(false);
    this.completionMessageState.set(message);
    this.isCompletedState.set(true);
  }

  private clearQuestion(): void {
    this.latestBotMessage = null;
    this.layoutState.set(null);
    this.questionTextState.set('');
    this.questionSubTextState.set('');
    this.imageOptionsState.set([]);
    this.buttonOptionsState.set([]);
    this.textPlaceholderState.set('');
    this.textCtaOptionState.set(null);
    this.photoCtaOptionState.set(null);
  }

  private computeQuestionOrdinal(messages: QuizChatMessage[], current: QuizChatMessage): number {
    let ordinal = 0;
    for (const message of messages) {
      const inputs = message.get('Inputs');
      const qualifies = message.get('Owner') === this.appConfig.Shared.Assistant.Owner.BOT
        && message.get('Mode') !== this.appConfig.Shared.Assistant.Mode.SMS_COPY
        && !this.requireConfig().skipQuestionIds.includes(message.get('questionId'))
        && Array.isArray(inputs) && inputs.length > 0;
      if (qualifies) ordinal += 1;
      if (message === current) break;
    }
    return Math.max(ordinal, 1);
  }

  private isPendingAnswersError(error: unknown): boolean {
    const candidate = error as { code?: number; message?: string };
    return candidate?.code === 412 && typeof candidate?.message === 'string'
      && candidate.message.includes('Please answer pending questions.');
  }

  private toErrorText(error: unknown): string {
    return (error as { message?: string })?.message || 'Something went wrong. Please retry.';
  }

  private requireConfig(): QuizChatConfig {
    if (!this.config) throw new Error('QuizChatService used before configure()');
    return this.config;
  }
}
